import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { Component, ChangeDetectionStrategy, Input, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { TrimValidator } from '../forms/custom-validators/trim-validator';
import { IsNumberValidator } from '../forms/custom-validators/is-number-validator';
import { catchError, tap } from 'rxjs/operators';
import { HttpError } from '@shared/models/contact-form-http-error';
import { validatePhoneNumber } from '../forms/custom-validators/phone-number.validator';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { isValidEmail } from '../forms/custom-validators/email-validator';
import { ContactFormService } from './services/contact-form.service';
import { TextInputComponent } from '../forms/text-input/text-input.component';
import { TextAreaComponent } from '../forms/text-area/text-area.component';
import { ButtonComponent } from '../button/button.component';
import { NgClass, AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-contact-form',
    templateUrl: './contact-form.component.html',
    styleUrls: ['./contact-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, TextInputComponent, TextAreaComponent, ButtonComponent, NgClass, AsyncPipe, TranslatePipe],
})
export class ContactFormComponent implements OnDestroy {
    private contactFormService = inject(ContactFormService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private translateService = inject(TranslateService);
    private formBuilder = inject(NonNullableFormBuilder);

    @Input() titleStyle = 'primary';

    isValid$ = new BehaviorSubject<boolean | null>(null);
    hasError = false;
    errorMessage$ = new BehaviorSubject<string | null>('');
    isSending$ = new BehaviorSubject<boolean>(false);

    subscriptions$ = new Subscription();

    contactForm = this.formBuilder.group({
        firstName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255), TrimValidator()]],
        lastName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255), TrimValidator()]],
        email: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(255), isValidEmail()]],
        nip: ['', [Validators.minLength(10), Validators.maxLength(10), TrimValidator(), IsNumberValidator()]],
        phone: ['', [validatePhoneNumber(), Validators.minLength(9)]],
        message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(1000), TrimValidator()]],
    });

    changeDisableState(): void {
        const controls = Object.keys(this.contactForm.controls);
        controls.forEach((control) => {
            if (this.isSending$.value) {
                this.contactForm.get(control)?.disable();
            } else {
                this.contactForm.get(control)?.enable();
            }
        });
    }

    restoreRequiredStatus(): void {
        const controls = Object.keys(this.contactForm.controls);
        controls.forEach((control) => {
            this.contactForm.get(control)?.setValue('');
            this.changeDetectorRef.detectChanges();
        });
    }

    onSubmit(): void {
        this.isSending$.next(true);
        this.changeDisableState();
        this.subscriptions$.add(
            this.contactFormService
                .sendMeetMessage(this.contactForm.getRawValue())
                .pipe(
                    catchError((response: HttpError) => {
                        switch (response.status) {
                            case 422: {
                                this.passErrorMessage('422');
                                break;
                            }
                            case 400: {
                                this.passErrorMessage('400');
                                break;
                            }
                            case 409: {
                                this.passErrorMessage('409');
                                break;
                            }
                            case 429: {
                                this.passErrorMessage('429');
                                break;
                            }
                            case 500: {
                                this.passErrorMessage('500');
                                break;
                            }
                        }
                        response?.error.errors.map((err: { title: string; propertyPath: string }) => {
                            const { title, propertyPath } = err;
                            this.contactForm.get(propertyPath)?.setErrors({ custom: title });
                        });
                        this.isSending$.next(false);
                        this.changeDisableState();
                        this.contactForm.reset();
                        this.changeDetectorRef.detectChanges();
                        return throwError(response);
                    }),
                    tap(() => {
                        this.isValid$.next(true);
                        this.isSending$.next(false);
                        this.changeDisableState();
                        this.contactForm.reset();
                        this.restoreRequiredStatus();
                    }),
                )
                .subscribe(),
        );
    }

    handleError(error: HttpError): Observable<HttpError> {
        return throwError(error.error?.errors);
    }

    closeFormAlert(): void {
        this.isValid$.next(null);
    }

    closeApiError(): void {
        this.errorMessage$.next(null);
        this.hasError = false;
    }

    passErrorMessage(errorCodeStatus: string): void {
        const translatedMessage = this.translateService.instant(`CONTACT.form_errors.${errorCodeStatus}`);
        this.errorMessage$.next(translatedMessage);
        this.hasError = true;
    }
    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }
}
